import { businessTimezone } from '#lib/stores/time.js';
import {
	type Business,
	type UpdateBusinessRequest,
	type Widget,
	BillingSanctionLevel,
	UserRole,
	WidgetAuthMode,
	WidgetButtonStyle,
	WidgetFlow,
	apiAppBusinessBusinessGetbusinesssettings,
	apiAppBusinessBusinessUpdatebusiness
} from '#lib/types/generated/index.js';
import { optimisticSet } from '#lib/stores/optimistic.js';
import { derived, get, writable } from 'svelte/store';
import { DateTime } from 'luxon';

export interface Person {
	name?: string | null;
	email?: string | null;
	phone?: string | null;
	firstName?: string | null;
	lastName?: string | null;
}

export function createDefaultWidget(): Widget {
	return {
		id: 0,
		name: null,
		businessName: null,
		hash: '',
		url: '',
		logo: null,
		hasBanner: false,
		theme: {
			fontFamily: 'default',
			buttonStyle: WidgetButtonStyle.Rounded,
			showLogo: true,
			showBusinessName: true,
			inversedHeaderTextColor: false,
			banner: null,
			backgroundImage: null,
			pageBackgroundColor: null,
			primary: '#00b300',
			background: '#ffffff',
			useDarkMode: false,
			logo: null,
			dark: {
				primary: '#00b300',
				background: '#202020',
				inversedHeaderTextColor: false,
				pageBackgroundColor: null
			}
		},
		isActive: true,
		isDefault: true,
		termsLink: null,
		termsContent: null,
		customContent: {
			welcome: null,
			activity: null,
			activityTitle: null,
			offer: null,
			offerTitle: null,
			pax: null,
			hub: null,
			prices: null,
			products: null,
			customer: null,
			promoCodes: null,
			validation: null,
			pay: null,
			thankYou: null,
			myBooking: null,
			reconfirmBooking: null,
			giftcardSelect: null,
			packageSelect: null
		},
		flowSelected: WidgetFlow.Flow1,
		paymentEnabled: false,
		showCustomerRequestInput: false,
		showCollectionInGrid: false,
		googleTagManagerId: '',
		isWhitelabel: false,
		maxFutureBookingDays: null,
		memberArea: {
			authMode: WidgetAuthMode.None,
			authMandatory: false,
			oktaIssuer: null,
			oktaClientId: null,
			jwksUrl: null,
			jwksIssuer: null
		},
		email: {
			showLogo: true,
			headerText: '',
			footerText: '',
			customContent: {},
			banner: '',
			logo: ''
		},
		contact: {
			address: null,
			useOwnerContact: true,
			contactName: null,
			contactPhone: null,
			contactEmail: null
		},
		sms: {
			shortBusinessName: null
		},
		offers: null,
		bookableGiftcards: null,
		packages: null,
		googleReserve: null
	};
}

export function createDefaultBusiness(): Business {
	return {
		id: '',
		name: '',
		logo: '',
		address: '',
		country: 'FR',
		website: null,
		googleReviewsUrl: null,
		timezone: 'Europe/Paris',
		fontFamily: null,
		registrationDate: DateTime.now(),
		widgets: [createDefaultWidget()],
		contractIsActive: false,
		sanctionLevel: BillingSanctionLevel.Zero,
		billingEmails: [],
		isStripeLegacyIndirect: false,
		isSplitPaymentEnabled: false,
		splitPaymentThreshold: 0,
		splitPaymentDelay: 0,
		splitPaymentOwnerFee: 0,
		splitPaymentFee: 0,
		owner: {
			id: '',
			email: '',
			firstName: '',
			lastName: '',
			phone: '',
			role: UserRole.Admin,
			locale: 'fr',
			defaultBusinessId: '',
			releaseNotesReadAt: DateTime.now(),
			doNotDisturbUntil: null,
			cguAcceptedAt: null,
			termsAcceptedAt: null,
			privacyAcceptedAt: null
		},
		defaultOfferCollectionId: '',
		package: ''
	};
}

export const businessStore = writable<Business>(createDefaultBusiness());

export function refreshBusiness(callback?: () => void): void {
	apiAppBusinessBusinessGetbusinesssettings().then((business) => {
		businessStore.set(business);
		businessTimezone.set(business.timezone);
		if (callback) {
			callback();
		}
	});
}

export function getPreferredContact(): Person | null {
	const business = get(businessStore);
	const defaultWidget = get(widgetDefault);

	if (defaultWidget.contact.useOwnerContact) {
		return business.owner;
	}

	return {
		name: defaultWidget.contact.contactName,
		phone: defaultWidget.contact.contactPhone,
		email: defaultWidget.contact.contactEmail
	};
}

export function getBusinessLogo(): string {
	return get(widgetDefault).logo || '/favicon.ico';
}

export const widgetDefault = derived(businessStore, ($businessStore) => {
	return $businessStore.widgets.find((w) => w.isDefault)!;
});

export function saveBusiness(business: Business, updateCountry?: boolean): Promise<Business> {
	const payload: UpdateBusinessRequest = {
		name: business.name,
		address: business.address,
		country: business.country,
		website: business.website ?? '',
		googleReviewsUrl: business.googleReviewsUrl,
		owner: {
			firstName: business.owner.firstName,
			lastName: business.owner.lastName,
			email: business.owner.email,
			phone: business.owner.phone
		},
		billingEmails: business.billingEmails,
		isSplitPaymentEnabled: business.isSplitPaymentEnabled,
		splitPaymentThreshold: business.splitPaymentThreshold,
		splitPaymentDelay: business.splitPaymentDelay,
		splitPaymentOwnerFee: business.splitPaymentOwnerFee,
		updateCountry: updateCountry
	};
	return optimisticSet(businessStore, business, () =>
		apiAppBusinessBusinessUpdatebusiness(payload).then((response) => {
			businessStore.set(response);
			businessTimezone.set(response.timezone);
			return get(businessStore);
		})
	);
}
